/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBasket } from '@/providers/basket';
import { Switch } from '@/components/forms/switch';
import { Typography } from '@/components/typography';
import { cn } from '@/lib/utils';
import { useCheckoutActions } from '@/hooks/use-checkout-actions';

/**
 * Minimal structural shape the component consumes from `useCheckoutActions`.
 * Defined locally to keep the test seam loose (mirrors CombineDeliveriesToggle).
 */
export interface FewerDeliveriesToggleActions {
    submitCombineDeliveries: (enabled: boolean) => void;
    combineDeliveriesFetcher: { state: 'idle' | 'loading' | 'submitting' };
}

export interface FewerDeliveriesToggleProps {
    /** Optional class name for the outer container. */
    className?: string;
    /**
     * Test seam: lets unit tests inject a stub for `useCheckoutActions` rather
     * than mocking the entire module. The default uses the real hook.
     */
    useActions?: () => FewerDeliveriesToggleActions;
}

const TITLE_ID = 'fewer-deliveries-title';

/**
 * "Reduce your items in fewer deliveries" promo banner shown below the
 * Delivery Method heading on the checkout page (Figma node 34613-66829).
 *
 * Functionally identical to CombineDeliveriesToggle — reads the same basket
 * attributes (`c_combineDeliveriesEligible`, `c_cartHasMMCCombined`) and fires
 * the same `submitCombineDeliveries` action. Differs only in visual styling:
 * truck icon, `bg-secondary` card, uppercase eyebrow title.
 *
 * Renders null when the basket is not combine-eligible.
 */
export default function FewerDeliveriesToggle({
    className,
    useActions = useCheckoutActions,
}: FewerDeliveriesToggleProps) {
    const { t } = useTranslation('checkout');
    const basket = useBasket();
    const { submitCombineDeliveries, combineDeliveriesFetcher } = useActions();

    const eligible = basket?.c_combineDeliveriesEligible === true;
    if (!eligible) {
        return null;
    }

    const applied = basket?.c_cartHasMMCCombined === true;
    const isSubmitting = combineDeliveriesFetcher.state === 'submitting';

    return (
        <div
            className={cn('flex flex-col gap-2 rounded-md bg-secondary p-4', className)}
            data-testid="fewer-deliveries-banner">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <Truck className="mt-0.5 size-5 shrink-0 text-foreground" aria-hidden="true" />
                    <div className="flex flex-col gap-1">
                        <span
                            id={TITLE_ID}
                            className="text-xs font-semibold uppercase leading-4 tracking-button text-foreground">
                            {t('shipping.fewerDeliveries.title')}
                        </span>
                        <p className="text-sm font-normal leading-5 text-muted-foreground">
                            {t('shipping.fewerDeliveries.description')}
                        </p>
                    </div>
                </div>
                <Switch
                    checked={applied}
                    onCheckedChange={(checked) => submitCombineDeliveries(checked === true)}
                    disabled={isSubmitting}
                    aria-labelledby={TITLE_ID}
                    aria-label={t('shipping.fewerDeliveries.toggleLabel')}
                    aria-busy={isSubmitting}
                    className="mt-0.5 shrink-0"
                    data-testid="fewer-deliveries-toggle"
                />
            </div>
            {isSubmitting && (
                <Typography variant="small" className="text-muted-foreground" aria-live="polite">
                    {t('shipping.combineDeliveries.updating')}
                </Typography>
            )}
        </div>
    );
}
